import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { ApiError, DetailView } from '@pro4-erp/core-web';
import { Box, CircularProgress } from '@mui/material';
import { getMessageFromError } from '@/core';
import { useDetailPageStore } from './useDetailPageStore';
import Form from '../../components/Form';
import { getAuditorProposalById } from '../../api/getAuditorProposalsById';
import { AuditProposalDetail } from '../../types';
import { updateAuditorProposal } from '../../api/updateAuditorProposal';
import CompetencesEdit from './CompetencesEdit';

const DetailPage = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>() as { id: string };

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const detailPageStore = useDetailPageStore();

  const query = useQuery<AuditProposalDetail, ApiError>(
    ['auditor-proposal-id', id],
    () => getAuditorProposalById(id),
    { refetchOnWindowFocus: false }
  );

  const { mutate } = useMutation({
    mutationFn: updateAuditorProposal,
    onSuccess: () => {
      enqueueSnackbar(
        t('Successfully saved changes.', { defaultValue: 'Successfully saved changes.' }),
        { variant: 'success' }
      );
      queryClient.invalidateQueries(['auditor-proposal-id', id]);
      navigate('/auditor-proposals');
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
  });

  useEffect(() => {
    return () => {
      detailPageStore.reset();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!query.data) {
      return;
    }

    detailPageStore.setForm({
      note: query.data.note || '',
      workExperience: query.data.workExperience || '',
      education: query.data.education || '',
      training: query.data.training || '',
      workplaceTraining: query.data.workplaceTraining || '',
      statusNote: query.data.statusNote || '',
      status: query.data.status || null,
      auditorCategory: query.data.auditorCategory || null,
      standard: query.data.standard || null,
      class: query.data.class || null,
    });
    detailPageStore.setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data]);

  if (query.isLoading || detailPageStore.isLoading) {
    return (
      <Box>
        <Box
          sx={{
            width: '100vw',
            height: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CircularProgress size={100} />
        </Box>
      </Box>
    );
  }

  if (query.isError) {
    return <p>{query.error.message}</p>;
  }

  return (
    <Box sx={{ mt: 1 }}>
      <DetailView
        title={<DetailView.Title>{query.data.note || 'N/A'}</DetailView.Title>}
        onGoBack={() => navigate('/auditor-proposals')}
        tabs={[
          {
            id: 'details',
            title: t('Details', { defaultValue: 'Details' }),
          },
          {
            id: 'competences',
            title: t('Competences', { defaultValue: 'Competences' }),
          },
        ]}
        defaultTab="details"
        childrenSx={{ padding: '2rem' }}
      >
        {({ TabContent }) => (
          <>
            <TabContent tab="details">
              <Form
                onSubmit={(data) => mutate({ id: query.data.id, data })}
                initialState={detailPageStore.form}
                hasFormChanged={(form) => {
                  if (
                    form.note === query.data.note &&
                    form.workExperience === query.data.workExperience &&
                    form.education === query.data.education &&
                    form.training === query.data.training &&
                    form.workplaceTraining === query.data.workplaceTraining &&
                    form.auditorCategory === query.data.auditorCategory &&
                    form.class === query.data.class &&
                    form.standard === query.data.standard
                  ) {
                    return false;
                  }

                  return true;
                }}
                onUnmount={(form) => detailPageStore.setForm({ ...form })}
                isFormEditable={query.data.isFormEditable}
              />
            </TabContent>
            <TabContent tab="competences">
              <CompetencesEdit
                auditorProposalId={query.data.id}
                competences={query.data.auditorProposalComps}
                isFormEditable={query.data.isFormEditable}
              />
            </TabContent>
          </>
        )}
      </DetailView>
    </Box>
  );
};

export default DetailPage;
